import { api } from '@/lib/api/client'
import { normalizeAffiliate, normalizeAffiliateCredential } from '@/lib/affiliate-normalize'
import type {
  CreateAffiliateInput,
  CreateAffiliateResponse,
  FetchAffiliateBalanceTransactionsResponse,
  FetchAffiliatePaymentsParams,
  FetchAffiliatePaymentsResponse,
  FetchAffiliatesResponse,
  SearchPlatformUserForAffiliateResponse,
  UpdateAffiliateInput,
} from '@/types/affiliates'

export async function fetchAffiliates(): Promise<FetchAffiliatesResponse> {
  const res = await api.get<FetchAffiliatesResponse>('/api/institution/affiliates', {
    requireAuth: true,
  })
  return {
    ...res,
    affiliates: res.affiliates.map(normalizeAffiliate),
  }
}

export async function searchPlatformUserForAffiliate(input: {
  email?: string
  phone?: string
}): Promise<SearchPlatformUserForAffiliateResponse> {
  return api.post<SearchPlatformUserForAffiliateResponse>(
    '/api/institution/affiliates/users/search',
    input,
    { requireAuth: true }
  )
}

export async function createAffiliate(
  input: CreateAffiliateInput
): Promise<CreateAffiliateResponse> {
  const res = await api.post<CreateAffiliateResponse>('/api/institution/affiliates', input, {
    requireAuth: true,
  })
  return { affiliate: normalizeAffiliateCredential(res.affiliate) }
}

export async function updateAffiliate(
  id: number,
  input: UpdateAffiliateInput
): Promise<CreateAffiliateResponse> {
  const res = await api.put<CreateAffiliateResponse>(`/api/institution/affiliates/${id}`, input, {
    requireAuth: true,
  })
  return { affiliate: normalizeAffiliateCredential(res.affiliate) }
}

export async function fetchAffiliatePayments(
  params: FetchAffiliatePaymentsParams = {}
): Promise<FetchAffiliatePaymentsResponse> {
  const searchParams = new URLSearchParams()
  if (params.affiliate_id != null) searchParams.set('affiliate_id', String(params.affiliate_id))
  if (params.campaign_id != null) searchParams.set('campaign_id', String(params.campaign_id))
  if (params.page != null) searchParams.set('page', String(params.page))
  if (params.per_page != null) searchParams.set('per_page', String(params.per_page))

  const query = searchParams.toString()
  return api.get<FetchAffiliatePaymentsResponse>(
    `/api/institution/affiliates/payments${query ? `?${query}` : ''}`,
    { requireAuth: true }
  )
}

export async function fetchAffiliateBalanceTransactions(
  affiliateId: number,
  params: { page?: number; per_page?: number } = {}
): Promise<FetchAffiliateBalanceTransactionsResponse> {
  const searchParams = new URLSearchParams()
  if (params.page != null) searchParams.set('page', String(params.page))
  if (params.per_page != null) searchParams.set('per_page', String(params.per_page))
  const q = searchParams.toString()
  return api.get<FetchAffiliateBalanceTransactionsResponse>(
    `/api/institution/affiliates/${affiliateId}/balance-transactions${q ? `?${q}` : ''}`,
    { requireAuth: true }
  )
}

export async function recordAffiliatePayout(
  affiliateId: number,
  input: { amount: number; note?: string | null }
): Promise<CreateAffiliateResponse> {
  return api.post<CreateAffiliateResponse>(
    `/api/institution/affiliates/${affiliateId}/payout`,
    input,
    { requireAuth: true }
  )
}
